import {
    AutocompleteApi,
    AutocompleteState,
    BaseItem
} from "@algolia/autocomplete-core";
import { isDefined } from "@omni/utils/helper.utils";
import { cn } from "@omni/utils/styles";
import {
    BaseSyntheticEvent,
    ChangeEvent,
    FormEvent,
    ForwardedRef,
    forwardRef,
    KeyboardEvent,
    MouseEvent,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef
} from "react";

import { AutocompleteRef } from "@/components/Search/SearchBar/searchBar.types";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { preventUntrusted } from "@/utils/domElements";
import { KeyNames } from "@/utils/enums";
import { createKeyHandler } from "@/utils/keyboard";

import { getLastAutocompleteItem } from "../../helpers/accessibility.helpers";
import { AutocompleteActions } from "../../useAutocompleteApi.hook";
import "./searchBox.scss";

import { isValidQuery } from "@/redux/modules/value/search/helpers/search.helpers";

import focusElementByRef from "./focus.helper";

type AutocompleteSearchBoxComponentProps = {
    isOpen: boolean;
    autocomplete: AutocompleteApi<
        any,
        BaseSyntheticEvent,
        MouseEvent,
        KeyboardEvent
    >;
    autocompleteState?: AutocompleteState<BaseItem>;
    open: () => void;
    close: () => void;
    autocompleteActions?: AutocompleteActions;
};

const SearchBoxComponent = (
    {
        isOpen,
        autocomplete,
        autocompleteState,
        open,
        close,
        autocompleteActions
    }: AutocompleteSearchBoxComponentProps,
    ref: ForwardedRef<AutocompleteRef>
) => {
    const { t } = useGranite();

    const inputRef = useRef<HTMLInputElement>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const resetButtonRef = useRef<HTMLButtonElement>(null);
    const submitButtonRef = useRef<HTMLButtonElement>(null);
    const queryRef = useRef<string | null>(null);

    const props = {
        form: autocomplete.getFormProps({
            inputElement: inputRef.current
        }),
        input: autocomplete.getInputProps({
            inputElement: inputRef.current
        })
    };

    const forceFocusOnInputRef = useCallback(() => {
        const timeout = focusElementByRef(inputRef);
        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, []);

    const handleOnInputKeyDown = createKeyHandler<KeyboardEvent>([
        {
            keys: [KeyNames.ENTER],
            handler: (event) => {
                const activeItemId = autocompleteState?.activeItemId;
                if (isDefined(activeItemId)) return;

                event.stopPropagation();

                if (!isValidQuery(inputRef.current?.value)) {
                    event.preventDefault();
                } else {
                    props.input.onKeyDown(event);
                }
            }
        },
        {
            keys: [KeyNames.TAB],
            handler: (event) => {
                if (!event.shiftKey) return;

                const lastAutocompleteItem = getLastAutocompleteItem();

                if (!isDefined(lastAutocompleteItem)) return;

                event.preventDefault();
                lastAutocompleteItem.focus();
            }
        },
        {
            defaultExcept: [KeyNames.TAB],
            handler: (event) => props.input.onKeyDown(event)
        }
    ]);

    const handleOnSubmit = (event: FormEvent) => {
        if (!isValidQuery(autocompleteState?.query) || !event.isTrusted) {
            event.preventDefault();
        } else {
            props.form.onSubmit(event);
        }
    };

    const handleOnResetClick = (event: MouseEvent) => {
        event.preventDefault();

        autocompleteState?.query && autocomplete.setQuery("");
        autocompleteActions?.setQuery("");

        open();
        forceFocusOnInputRef();
    };

    const handleOnCloseClick = (event: MouseEvent) => {
        event.stopPropagation();
        close();
    };

    const handleOnInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        queryRef.current = event.target.value;
        props.input.onChange(event);
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (isOpen) {
            forceFocusOnInputRef();
        }
    }, [isOpen]);

    useImperativeHandle(ref, () => ({
        get input() {
            return inputRef.current;
        },
        get form() {
            return formRef.current;
        },
        get query() {
            return queryRef.current;
        },
        set query(query: string | null) {
            queryRef.current = query;
        }
    }));

    return (
        <div className={cn("d-flex align-items-center")}>
            {isOpen && (
                <button
                    type={"button"}
                    className={cn("searchBox__close d-lg-none")}
                    onClick={handleOnCloseClick}
                    tabIndex={isOpen ? 0 : -1}
                    aria-label={t("accessibility.searchBox.close")}
                >
                    <i className={cn("las la-lg la-arrow-left")}></i>
                </button>
            )}
            <form
                {...props.form}
                className={cn(
                    "searchBox border-primary border position-relative tw:flex tw:flex-wrap mx-0 overflow-hidden flex-grow-1",
                    {
                        "searchBox--active": isOpen
                    }
                )}
                ref={formRef}
                onSubmit={handleOnSubmit}
            >
                <div
                    className={cn(
                        "searchBox__wrapper col-9 col-sm-10 col-md-11 col-lg-10 d-flex p-0"
                    )}
                >
                    <button
                        className={cn(
                            "searchBox__searchIcon border-0 d-none pl-3 d-lg-block"
                        )}
                        type="button"
                        onClick={open}
                        tabIndex={-1}
                        aria-label={t("accessibility.searchBox.open")}
                    >
                        <i className={cn("las la-lg la-search")}></i>
                    </button>
                    <input
                        id={"autocomplete-input"}
                        className={cn("tab flex-grow-1 border-0 pl-3 pl-lg-2")}
                        ref={inputRef}
                        type={props.input.type}
                        value={props.input.value ?? ""}
                        tabIndex={0}
                        autoComplete={props.input.autoComplete}
                        autoCapitalize={"none"}
                        autoCorrect={props.input.autoCorrect}
                        enterKeyHint={props.input.enterKeyHint}
                        maxLength={props.input.maxLength}
                        placeholder={props.input.placeholder}
                        spellCheck={props.input.spellCheck}
                        onBlur={props.input.onBlur}
                        onChange={handleOnInputChange}
                        onKeyDown={handleOnInputKeyDown}
                        onClick={() => {
                            forceFocusOnInputRef();
                            props.input.onClick;
                        }}
                        onCompositionEnd={props.input.onCompositionEnd}
                        onFocus={props.input.onFocus}
                        aria-controls={props.input["aria-controls"]}
                        aria-autocomplete={props.input["aria-autocomplete"]}
                        aria-activedescendant={
                            props.input["aria-activedescendant"]
                        }
                        aria-label={t("accessibility.searchBox.label")}
                    />
                    {autocompleteState?.query && (
                        <button
                            className={cn(
                                "searchBox__button--reset border-0 px-3"
                            )}
                            ref={resetButtonRef}
                            type={"reset"}
                            onClick={handleOnResetClick}
                            tabIndex={isOpen ? 0 : -1}
                            aria-label={t("searchBox.input.button.reset")}
                        >
                            <span className={cn("d-none d-lg-block")}>
                                {t("searchBox.input.button.reset")}
                            </span>
                            <i
                                className={cn(
                                    "las la-lg la-times d-block d-lg-none"
                                )}
                            ></i>
                        </button>
                    )}
                </div>
                <button
                    className={cn(
                        "tw:bg-primary border-0 text-white col-3 col-sm-2 col-md-1 col-lg-2"
                    )}
                    onClick={preventUntrusted()}
                    type="submit"
                    tabIndex={0}
                    ref={submitButtonRef}
                    aria-label={t("searchBox.input.button.submit")}
                >
                    <span
                        id="submit-search-button"
                        className={cn("d-none d-lg-block")}
                    >
                        {t("searchBox.input.button.submit")}
                    </span>
                    <i
                        className={cn("las la-lg la-search d-block d-lg-none")}
                    />
                </button>
            </form>
        </div>
    );
};

export default forwardRef(SearchBoxComponent);
